import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  alpha,
  Button,
  Chip,
  Container,
  Typography,
  Box,
  useTheme,
  Fade,
  Grid,
  Avatar,
  Skeleton,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  AccountBalance,
  CalendarMonthOutlined,
  InsightsOutlined,
  ReceiptLongOutlined,
  ShowChart,
  LoginOutlined,
  SavingsOutlined,
  SwapHorizOutlined,
  TrendingDown,
  TrendingUp,
} from "@mui/icons-material";
import { getMonthsLongNames, getMonthIndex, getFirstDayOfMonth, getLastDayOfMonth } from "../../../utils/DateUtil";
import { useAuth } from "../../../auth/AuthContext";
import { LOGIN_ROUTE_PATH } from "../../../utils/constants/AppConstants";
import { useMonthlyReport } from "../../../hooks/reports";
import ExpensesMonthlyReportsComponent from "../../../components/finance/reports/monthly/ExpensesMonthlyReportsComponent";
import IncomesMonthlyReportsComponent from "../../../components/finance/reports/monthly/IncomesMonthlyReportsComponent";
import { PageContainer } from "../../../components/common/StyledComponents";
import SurfaceCard from '../../../components/common/ui/SurfaceCard';
import PrimaryButton from '../../../components/common/ui/PrimaryButton';
import MonthYearPicker from "../../../components/common/MonthYearPicker";
import { useCurrencyFormatter } from "../../../contexts/CurrencyContext";
import MonthlyReportsCombinedTrendComponent from "../../../components/finance/reports/monthly/MonthlyReportsCombinedTrendComponent";

const getNumericEntries = (values: unknown): Array<[string, number]> => {
  if (values instanceof Map) {
    return Array.from(values.entries()).map(([key, value]) => [String(key), Number(value) || 0]);
  }

  if (!values || typeof values !== "object") {
    return [];
  }

  return Object.entries(values as Record<string, unknown>).map(([key, value]) => [key, Number(value) || 0]);
};

const MonthlyReportsPage: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const { isAuthenticated } = useAuth();
  const { formatCurrencyNumber: formatCurrency } = useCurrencyFormatter();

  const today = new Date();
  const currentYear = today.getFullYear();
  const currentMonthIndex = today.getMonth();
  const monthNames = getMonthsLongNames();

  const [searchParams, setSearchParams] = useSearchParams();
  const urlYear = Number(searchParams.get("year")) || currentYear;
  const urlMonth = searchParams.get("month") || monthNames[currentMonthIndex];

  const [selectedYear, setSelectedYear] = useState(urlYear);
  const [selectedMonth, setSelectedMonth] = useState(urlMonth);
  const [dataKey, setDataKey] = useState(0); // For triggering transitions
  const [analysisView, setAnalysisView] = useState<"expenses" | "incomes">("expenses");
  const accentSoft = `${theme.palette.primary.main}24`;

  const updateSearchParams = useCallback((newParams: Record<string, string>, replace = false) => {
    const updatedParams = new URLSearchParams(searchParams.toString());
    Object.entries(newParams).forEach(([key, value]) => {
      if (value) {
        updatedParams.set(key, value);
      } else {
        updatedParams.delete(key);
      }
    });
    setSearchParams(updatedParams, { replace });
  }, [searchParams, setSearchParams]);

  const startDate = getFirstDayOfMonth(selectedYear, getMonthIndex(selectedMonth));
  const endDate = getLastDayOfMonth(selectedYear, getMonthIndex(selectedMonth));
  const {
    data: report,
    isLoading,
    isFetching,
    error,
    refetch,
  } = useMonthlyReport(startDate, endDate);

  const handleYearChange = (year: number) => {
    setSelectedYear(year);
    updateSearchParams({ year: year.toString() });
    setDataKey(prev => prev + 1); // Trigger transition
  };

  const handleMonthChange = (month: string) => {
    setSelectedMonth(month);
    updateSearchParams({ month });
    setDataKey(prev => prev + 1); // Trigger transition
  };
  useEffect(() => {
    if (isAuthenticated) {
      const needsInit = !searchParams.get("year") || !searchParams.get("month");
      if (needsInit) {
        updateSearchParams({
          year: selectedYear.toString(),
          month: selectedMonth,
        }, true);
      }
    }
  }, [isAuthenticated, searchParams, selectedMonth, selectedYear, updateSearchParams]);

  // Calculate summary stats
  const totalExpenses = report?.reportInfo?.totalValues?.expensesTotalAmount || 0;
  const totalIncome = report?.reportInfo?.totalValues?.incomesTotalAmount || 0;
  const netBalance = totalIncome - totalExpenses;
  const expenseShare = totalIncome > 0 ? (totalExpenses / totalIncome) * 100 : 0;
  
  // Calculate total transactions from actual counts
  const expensesCount = report?.reportInfo?.totalValues?.expensesCount || 0;
  const incomesCount = report?.reportInfo?.totalValues?.incomesCount || 0;
  const totalTransactions = expensesCount + incomesCount;
  const hasData = totalTransactions > 0;

  const expenseCategoryEntries = getNumericEntries(report?.reportInfo?.expensesValuePerCategory);
  const incomeCategoryEntries = getNumericEntries(report?.reportInfo?.incomesValuePerCategory);
  const topExpenseCategory = [...expenseCategoryEntries].sort((a, b) => b[1] - a[1])[0];
  const topIncomeCategory = [...incomeCategoryEntries].sort((a, b) => b[1] - a[1])[0];
  const totalActiveCategories = expenseCategoryEntries.length + incomeCategoryEntries.length;
  const monthlyNarrative =
    !hasData
      ? `No transactions recorded for ${selectedMonth} ${selectedYear} yet.`
      : netBalance >= 0
        ? `You stayed positive this month with ${formatCurrency(netBalance)} left after expenses.`
        : `You closed the month with a ${formatCurrency(Math.abs(netBalance))} deficit. Review top expenses to rebalance.`;
  
  // Calculate savings rate (percentage of income saved)
  // Only calculate if there's income; otherwise show N/A
  const hasSavingsRate = totalIncome > 0;
  const savingsRate = hasSavingsRate ? ((netBalance / totalIncome) * 100) : 0;
  const savingsRateFormatted = savingsRate.toFixed(1);
  
  // Determine display for Savings Rate card
  const getSavingsRateDisplay = () => {
    // No data at all
    if (totalIncome === 0 && totalExpenses === 0) {
      return { 
        text: 'No Data', 
        status: 'neutral', 
        subtitle: 'Add transactions to track' 
      };
    }
    
    // Has expenses but no income - can't calculate a meaningful rate
    if (totalIncome === 0 && totalExpenses > 0) {
      return { 
        text: 'N/A', 
        status: 'insufficient',
        subtitle: 'No income to calculate rate'
      };
    }
    
    // Normal case: has income, show percentage
    const subtitle = savingsRate >= 20 
      ? '🎉 Excellent savings!' 
      : savingsRate >= 10 
      ? '👍 Good progress' 
      : savingsRate >= 0 
      ? '💪 Keep it up'
      : '⚠️ Overspending';
      
    return {
      text: `${savingsRateFormatted}%`,
      status: savingsRate >= 20 ? 'excellent' : savingsRate >= 0 ? 'good' : 'overspent',
      subtitle
    };
  };
  
  const savingsDisplay = getSavingsRateDisplay();
  const isBusy = isLoading || isFetching;

  if (!isAuthenticated) {
    return (
      <PageContainer>
        <Container maxWidth="md" sx={{ py: { xs: 4, md: 8 } }}>
          <SurfaceCard
            sx={{
              p: { xs: 3, md: 5 },
              textAlign: "center",
              position: "relative",
              overflow: "hidden",
              borderRadius: "18px",
              border: `1px solid ${accentSoft}`,
              background: `linear-gradient(165deg, ${theme.palette.background.paper}, ${theme.palette.background.default})`,
              "&::after": {
                content: '""',
                position: "absolute",
                inset: "auto -30% -65% auto",
                width: 420,
                height: 420,
                borderRadius: "50%",
                background: `radial-gradient(circle, ${accentSoft} 0%, transparent 60%)`,
              },
            }}
          >
            <Avatar
              sx={{
                width: 64,
                height: 64,
                mx: "auto",
                mb: 2,
                backgroundColor: accentSoft,
                color: theme.palette.primary.light,
              }}
            >
              <ShowChart />
            </Avatar>
            <Typography
              variant="h3"
              gutterBottom
              sx={{
                fontFamily: '"Space Grotesk", "Manrope", sans-serif',
                fontWeight: 700,
                letterSpacing: "-0.02em",
              }}
            >
              Monthly Financial Intelligence
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 640, mx: "auto", mb: 3 }}>
              Review monthly performance, identify trends, and understand where your money is accelerating or drifting.
            </Typography>
            <PrimaryButton startIcon={<LoginOutlined />} onClick={() => navigate(LOGIN_ROUTE_PATH)} sx={{ px: 4, py: 1.5 }}>
              Sign In to View Reports
            </PrimaryButton>
          </SurfaceCard>
        </Container>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <Container maxWidth="xl" sx={{ pt: { xs: 2, md: 4 }, pb: { xs: 5, md: 4 } }}>
        <Fade in={true} timeout={400}>
          <Box>
            <SurfaceCard
              sx={{
                mb: 3,
                p: { xs: 2.5, md: 4 },
                borderRadius: "18px",
                border: `1px solid ${accentSoft}`,
                background: `linear-gradient(145deg, ${theme.palette.background.paper}, ${theme.palette.background.default})`,
                position: "relative",
                overflow: "hidden",
                "&::before": {
                  content: '""',
                  position: "absolute",
                  inset: "-180px auto auto -120px",
                  width: 340,
                  height: 340,
                  borderRadius: "50%",
                  background: `radial-gradient(circle, ${accentSoft} 0%, transparent 66%)`,
                },
              }}
            >
              <Grid container spacing={3} alignItems="center" sx={{ position: "relative", zIndex: 1 }}>
                <Grid item xs={12} md={7}>
                  <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1.3 }}>
                    <Avatar sx={{ bgcolor: accentSoft, color: theme.palette.primary.light }}>
                      <InsightsOutlined />
                    </Avatar>
                    <Chip
                      icon={<CalendarMonthOutlined />}
                      label={`${selectedMonth} ${selectedYear}`}
                      sx={{
                        borderRadius: "10px",
                        fontWeight: 700,
                        backgroundColor: `${theme.palette.primary.main}18`,
                        color: theme.palette.primary.light,
                      }}
                    />
                    {isFetching && !isLoading && (
                      <Chip
                        size="small"
                        label="Refreshing"
                        sx={{
                          borderRadius: "10px",
                          backgroundColor: alpha(theme.palette.text.primary, 0.1),
                          color: theme.palette.text.secondary,
                        }}
                      />
                    )}
                  </Stack>
                  <Typography
                    variant="h3"
                    sx={{
                      fontFamily: '"Space Grotesk", "Manrope", sans-serif',
                      fontWeight: 700,
                      letterSpacing: "-0.02em",
                      mb: 1,
                    }}
                  >
                    Monthly Reports
                  </Typography>
                  <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 720, mb: 1.4 }}>
                    Analyze income, spending, and net direction in one place. Use this view to catch trends early and adjust before the next cycle.
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {monthlyNarrative}
                  </Typography>
                </Grid>

                <Grid item xs={12} md={5}>
                  <SurfaceCard
                    sx={{
                      p: 2,
                      borderRadius: "14px",
                      border: `1px solid ${alpha(theme.palette.text.primary, 0.08)}`,
                      background: alpha(theme.palette.background.default, 0.38),
                    }}
                  >
                    <MonthYearPicker
                      selectedMonth={selectedMonth}
                      selectedYear={selectedYear}
                      onMonthChange={handleMonthChange}
                      onYearChange={handleYearChange}
                      colorTheme="primary"
                      transactionCount={totalTransactions}
                      isLoading={isBusy}
                    />
                  </SurfaceCard>
                </Grid>
              </Grid>
            </SurfaceCard>

            {error && (
              <Alert
                severity="error"
                sx={{ mb: 3, borderRadius: 2 }}
                action={
                  <Button color="inherit" size="small" onClick={() => void refetch()}>
                    Retry
                  </Button>
                }
              >
                We could not load report data for this period. Please try again.
              </Alert>
            )}

            <Fade in={true} timeout={600} key={`summary-${dataKey}`}>
              <Grid container spacing={3} sx={{ mb: 6 }}>
                <Grid item xs={12} sm={6} md={3}>
                  <SurfaceCard
                    sx={{
                      p: 2.2,
                      height: "100%",
                      borderRadius: "14px",
                      border: `1px solid ${alpha(theme.palette.error.main, 0.28)}`,
                      background: alpha(theme.palette.error.main, 0.08),
                    }}
                  >
                    <Stack direction="row" spacing={1.2} alignItems="center" sx={{ mb: 1.2 }}>
                      <Avatar sx={{ bgcolor: alpha(theme.palette.error.main, 0.2), color: theme.palette.error.light }}>
                        <TrendingDown />
                      </Avatar>
                      <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                        Total Expenses
                      </Typography>
                    </Stack>
                    <Typography variant="h4" sx={{ fontWeight: 700, color: theme.palette.error.main, mb: 0.6 }}>
                      {isBusy ? <Skeleton width={120} /> : formatCurrency(totalExpenses)}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {expensesCount} transactions
                    </Typography>
                  </SurfaceCard>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <SurfaceCard
                    sx={{
                      p: 2.2,
                      height: "100%",
                      borderRadius: "14px",
                      border: `1px solid ${alpha(theme.palette.success.main, 0.28)}`,
                      background: alpha(theme.palette.success.main, 0.08),
                    }}
                  >
                    <Stack direction="row" spacing={1.2} alignItems="center" sx={{ mb: 1.2 }}>
                      <Avatar sx={{ bgcolor: alpha(theme.palette.success.main, 0.2), color: theme.palette.success.light }}>
                        <TrendingUp />
                      </Avatar>
                      <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                        Total Income
                      </Typography>
                    </Stack>
                    <Typography variant="h4" sx={{ fontWeight: 700, color: theme.palette.success.main, mb: 0.6 }}>
                      {isBusy ? <Skeleton width={120} /> : formatCurrency(totalIncome)}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {incomesCount} transactions
                    </Typography>
                  </SurfaceCard>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <SurfaceCard
                    sx={{
                      p: 2.2,
                      height: "100%",
                      borderRadius: "14px",
                      border: `1px solid ${alpha(netBalance >= 0 ? theme.palette.success.main : theme.palette.error.main, 0.28)}`,
                      background: alpha(netBalance >= 0 ? theme.palette.success.main : theme.palette.error.main, 0.08),
                    }}
                  >
                    <Stack direction="row" spacing={1.2} alignItems="center" sx={{ mb: 1.2 }}>
                      <Avatar
                        sx={{
                          bgcolor: alpha(netBalance >= 0 ? theme.palette.success.main : theme.palette.error.main, 0.2),
                          color: netBalance >= 0 ? theme.palette.success.light : theme.palette.error.light,
                        }}
                      >
                        <AccountBalance />
                      </Avatar>
                      <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                        Net Balance
                      </Typography>
                    </Stack>
                    <Typography
                      variant="h4"
                      sx={{
                        fontWeight: 700,
                        color: netBalance === 0 ? theme.palette.text.secondary : netBalance > 0 ? theme.palette.success.main : theme.palette.error.main,
                        mb: 0.6,
                      }}
                    >
                      {isBusy ? <Skeleton width={120} /> : `${netBalance > 0 ? "+" : ""}${formatCurrency(netBalance)}`}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {netBalance === 0 ? "Break-even month" : netBalance > 0 ? "Positive cash flow" : "Negative cash flow"}
                    </Typography>
                  </SurfaceCard>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <SurfaceCard
                    sx={{
                      p: 2.2,
                      height: "100%",
                      borderRadius: "14px",
                      border: `1px solid ${alpha(
                        savingsDisplay.status === "overspent"
                          ? theme.palette.error.main
                          : savingsDisplay.status === "excellent"
                            ? theme.palette.success.main
                            : theme.palette.warning.main,
                        0.28
                      )}`,
                      background: alpha(
                        savingsDisplay.status === "overspent"
                          ? theme.palette.error.main
                          : savingsDisplay.status === "excellent"
                            ? theme.palette.success.main
                            : theme.palette.warning.main,
                        0.08
                      ),
                    }}
                  >
                    <Stack direction="row" spacing={1.2} alignItems="center" sx={{ mb: 1.2 }}>
                      <Avatar
                        sx={{
                          bgcolor: alpha(
                            savingsDisplay.status === "overspent"
                              ? theme.palette.error.main
                              : savingsDisplay.status === "excellent"
                                ? theme.palette.success.main
                                : theme.palette.warning.main,
                            0.2
                          ),
                          color:
                            savingsDisplay.status === "overspent"
                              ? theme.palette.error.light
                              : savingsDisplay.status === "excellent"
                                ? theme.palette.success.light
                                : theme.palette.warning.light,
                        }}
                      >
                        <SavingsOutlined />
                      </Avatar>
                      <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                        Savings Rate
                      </Typography>
                    </Stack>
                    <Typography
                      variant="h4"
                      sx={{
                        fontWeight: 700,
                        color:
                          savingsDisplay.status === "overspent"
                            ? theme.palette.error.main
                            : savingsDisplay.status === "excellent"
                              ? theme.palette.success.main
                              : savingsDisplay.status === "insufficient" || savingsDisplay.status === "neutral"
                                ? theme.palette.text.secondary
                                : theme.palette.warning.main,
                        mb: 0.6,
                      }}
                    >
                      {isBusy ? <Skeleton width={100} /> : savingsDisplay.text}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {savingsDisplay.subtitle}
                    </Typography>
                  </SurfaceCard>
                </Grid>
              </Grid>
            </Fade>

            <Grid container spacing={3} sx={{ mb: 4 }}>
              <Grid item xs={12} md={4}>
                <SurfaceCard sx={{ p: 2.2, borderRadius: "14px", height: "100%" }}>
                  <Stack direction="row" spacing={1.2} alignItems="center" sx={{ mb: 1 }}>
                    <Avatar sx={{ width: 34, height: 34, bgcolor: alpha(theme.palette.primary.main, 0.2), color: theme.palette.primary.light }}>
                      <ReceiptLongOutlined fontSize="small" />
                    </Avatar>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                      Monthly Activity
                    </Typography>
                  </Stack>
                  <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5 }}>
                    {isBusy ? <Skeleton width={80} /> : totalTransactions}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    total transactions recorded this month
                  </Typography>
                </SurfaceCard>
              </Grid>

              <Grid item xs={12} md={4}>
                <SurfaceCard sx={{ p: 2.2, borderRadius: "14px", height: "100%" }}>
                  <Stack direction="row" spacing={1.2} alignItems="center" sx={{ mb: 1 }}>
                    <Avatar sx={{ width: 34, height: 34, bgcolor: alpha(theme.palette.warning.main, 0.2), color: theme.palette.warning.light }}>
                      <SwapHorizOutlined fontSize="small" />
                    </Avatar>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                      Expense Pressure
                    </Typography>
                  </Stack>
                  <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5 }}>
                    {isBusy ? <Skeleton width={90} /> : totalIncome > 0 ? `${expenseShare.toFixed(1)}%` : "N/A"}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    of income consumed by expenses
                  </Typography>
                </SurfaceCard>
              </Grid>

              <Grid item xs={12} md={4}>
                <SurfaceCard sx={{ p: 2.2, borderRadius: "14px", height: "100%" }}>
                  <Stack direction="row" spacing={1.2} alignItems="center" sx={{ mb: 1 }}>
                    <Avatar sx={{ width: 34, height: 34, bgcolor: alpha(theme.palette.info.main, 0.2), color: theme.palette.info.light }}>
                      <InsightsOutlined fontSize="small" />
                    </Avatar>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                      Top Signals
                    </Typography>
                  </Stack>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 0.4 }}>
                    {isBusy
                      ? "Loading top categories..."
                      : topExpenseCategory
                        ? `Highest expense: ${topExpenseCategory[0]} (${formatCurrency(topExpenseCategory[1])})`
                        : "No expense category data yet."}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {isBusy
                      ? ""
                      : topIncomeCategory
                        ? `Highest income: ${topIncomeCategory[0]} (${formatCurrency(topIncomeCategory[1])})`
                        : "No income category data yet."}
                  </Typography>
                </SurfaceCard>
              </Grid>
            </Grid>

            {report && hasData ? (
              <Fade in={true} timeout={800} key={`reports-${dataKey}`}>
                <SurfaceCard sx={{ borderRadius: "16px", overflow: "hidden", border: `1px solid ${alpha(theme.palette.primary.main, 0.24)}` }}>
                  <Box
                    sx={{
                      p: 3,
                      borderBottom: `1px solid ${theme.palette.divider}`,
                      background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.12)} 0%, ${alpha(theme.palette.background.paper, 0.3)} 100%)`,
                      display: "flex",
                      flexDirection: { xs: "column", md: "row" },
                      justifyContent: "space-between",
                      gap: 1.6,
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.6 }}>
                      <Avatar sx={{ width: 40, height: 40, bgcolor: alpha(theme.palette.primary.main, 0.2), color: theme.palette.primary.light }}>
                        <InsightsOutlined />
                      </Avatar>
                      <Box>
                        <Typography variant="h6" sx={{ fontWeight: 700 }}>
                          Detailed Analysis Workspace
                        </Typography>
                      </Box>
                    </Box>

                    <ToggleButtonGroup
                      value={analysisView}
                      exclusive
                      onChange={(_, next) => {
                        if (next) setAnalysisView(next);
                      }}
                      size="small"
                      sx={{
                        alignSelf: { xs: "flex-start", md: "center" },
                        "& .MuiToggleButton-root": {
                          textTransform: "none",
                          px: 1.8,
                          borderColor: alpha(theme.palette.text.primary, 0.16),
                          color: theme.palette.text.secondary,
                        },
                        "& .Mui-selected": {
                          color: theme.palette.text.primary,
                          bgcolor: alpha(theme.palette.primary.main, 0.2),
                        },
                      }}
                    >
                      <ToggleButton value="expenses">
                        <Stack direction="row" spacing={0.8} alignItems="center">
                          <TrendingDown fontSize="small" />
                          <span>Expense Analysis</span>
                        </Stack>
                      </ToggleButton>
                      <ToggleButton value="incomes">
                        <Stack direction="row" spacing={0.8} alignItems="center">
                          <TrendingUp fontSize="small" />
                          <span>Income Analysis</span>
                        </Stack>
                      </ToggleButton>
                    </ToggleButtonGroup>
                  </Box>

                  <Box sx={{ p: { xs: 1.4, md: 2.2 } }}>
                    {analysisView === "expenses" ? (
                      <ExpensesMonthlyReportsComponent
                        year={selectedYear}
                        month={selectedMonth}
                        report={report}
                      />
                    ) : (
                      <IncomesMonthlyReportsComponent
                        year={selectedYear}
                        month={selectedMonth}
                        report={report}
                      />
                    )}
                  </Box>
                </SurfaceCard>
              </Fade>
            ) : (
              <SurfaceCard sx={{ p: { xs: 3.2, md: 5.2 }, textAlign: 'center', borderRadius: "16px" }}>
                <Avatar sx={{ width: 56, height: 56, mx: "auto", mb: 1.5, bgcolor: alpha(theme.palette.primary.main, 0.18), color: theme.palette.primary.light }}>
                  <ShowChart />
                </Avatar>
                <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.6 }}>
                  No report data for this period
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2.2, maxWidth: 560, mx: "auto" }}>
                  Add income and expense transactions in {selectedMonth} {selectedYear} to unlock monthly trend analysis, category breakdowns, and performance insights.
                </Typography>
                <Stack direction="row" spacing={1} justifyContent="center" sx={{ mb: 2 }}>
                  <Chip size="small" label={`${totalActiveCategories} active categories`} />
                  <Chip size="small" label={`${totalTransactions} transactions`} />
                </Stack>
                <PrimaryButton variant="outlined" onClick={() => void refetch()}>
                  Refresh Data
                </PrimaryButton>
              </SurfaceCard>
            )}

            {report && hasData && (
              <SurfaceCard
                sx={{
                  mt: 4,
                  borderRadius: "16px",
                  overflow: "hidden",
                  border: `1px solid ${alpha(theme.palette.primary.main, 0.28)}`,
                }}
              >
                <Box
                  sx={{
                    p: 3,
                    borderBottom: `1px solid ${theme.palette.divider}`,
                    background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.14)} 0%, ${alpha(theme.palette.background.paper, 0.35)} 100%)`,
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.6 }}>
                    <Avatar sx={{ width: 40, height: 40, bgcolor: alpha(theme.palette.primary.main, 0.2), color: theme.palette.primary.light }}>
                      <ShowChart />
                    </Avatar>
                    <Box>
                      <Typography variant="h6" sx={{ fontWeight: 700 }}>
                        Cashflow Timeline
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        One combined daily view for income, expenses, and net movement. Use it to spot turning points and volatility quickly.
                      </Typography>
                    </Box>
                  </Box>
                </Box>
                <Box sx={{ p: { xs: 2, md: 2.8 } }}>
                  <MonthlyReportsCombinedTrendComponent report={report} />
                </Box>
              </SurfaceCard>
            )}
          </Box>
        </Fade>
      </Container>
    </PageContainer>
  );
};

export default MonthlyReportsPage;
